const SELF_HANDLED_TARGETS = 'input, textarea, select, [contenteditable="true"], [role="dialog"]';

// Match the state, not just the slot: SheetContent and the Radix contents stay
// mounted while closed (the sheet for its slide-out, Radix for its exit
// animation), so the slot alone reports a modal on every page that renders a
// closed one, which is every page with a step rail.
const OPEN_MODAL = ['dialog-content', 'alert-dialog-content', 'sheet-content', 'drawer-content']
   .map(slot => `[data-slot="${slot}"][data-state="open"]`)
   .join(', ');

/** The user is typing, or focus sits in a panel that handles its own keys. */
export function isSelfHandledTarget(event: Event): boolean {
   const target = event.target;
   return target instanceof Element && target.closest(SELF_HANDLED_TARGETS) !== null;
}

/**
 * A modal is open over the editor, so the editor is not what the keys are
 * for. Answered from the document rather than from the event's target: the
 * media manager's markup dialog is a fabric.js canvas in a MooTools modal
 * with no role="dialog" and nothing focused, so a target-based check missed
 * it and arrows meant to nudge a marker walked tabs and steps underneath.
 *
 * body.modal-displayed covers the legacy modal from the click, while its
 * content is still loading; .modalBox.modalOpen catches the noOverlay case,
 * which never sets that class (Shared/modal.js, Shared/loading_status.js).
 */
export function isModalOpen(): boolean {
   if (document.body.classList.contains('modal-displayed')) {
      return true;
   }
   if (document.querySelector('.modalBox.modalOpen') !== null) {
      return true;
   }
   // The mobile step rail is a sheet the desktop layout hides with md:hidden.
   // Left open at phone width and then widened, it would otherwise keep every
   // shortcut off with nothing on screen to close.
   return [...document.querySelectorAll(OPEN_MODAL)].some(
      modal => getComputedStyle(modal).display !== 'none'
   );
}

/** The keystroke belongs to something other than the editor's shortcuts. */
export function isShortcutClaimed(event: Event): boolean {
   return isSelfHandledTarget(event) || isModalOpen();
}
